import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Lock } from 'lucide-react'
import { Checkbox } from '@/components/ui/Checkbox'
import { selectRoomPlayers } from '@/redux/selectors/roomSelector'
import type { GameroomEvent } from '@/redux/types/room'

interface EventAudienceFieldProps {
  index: number
  /** Locked once the event has gone out — players have already seen it. */
  disabled: boolean
}

/**
 * Who an event reaches. Defaults to everyone; narrowing it reveals the roster.
 */
export function EventAudienceField({
  index,
  disabled,
}: EventAudienceFieldProps) {
  const { values, setFieldValue } = useFormikContext<{
    events: GameroomEvent[]
  }>()
  const players = useSelector(selectRoomPlayers)

  const event = values.events[index]
  const affectsAll = event?.affects_all_players ?? true
  const selected: number[] =
    event?.assigned_player_ids ?? event?.assigned_players ?? []

  const setAffectsAll = (all: boolean) => {
    setFieldValue(`events[${index}].affects_all_players`, all)
    if (all) {
      setFieldValue(`events[${index}].assigned_player_ids`, [])
    }
  }

  const togglePlayer = (memberId: number) => {
    const next = selected.includes(memberId)
      ? selected.filter((id) => id !== memberId)
      : [...selected, memberId]
    setFieldValue(`events[${index}].assigned_player_ids`, next)
  }

  // member_id is the user id the server matches on. It is typed as string
  // here but the API sends a number, so normalise before comparing.
  const assignable = players
    .filter((p) => p.member_id !== undefined && p.member_id !== null)
    .map((p) => ({ ...p, memberId: Number(p.member_id) }))
  const noneChosen = !affectsAll && selected.length === 0

  return (
    <div className='p-3 space-y-3 rounded-md border border-gray-200 bg-gray-50/50'>
      <div className='flex gap-2 justify-between items-center'>
        <span className='text-sm font-medium text-dark-blue'>Who sees this</span>
        {disabled && (
          <span className='flex gap-1 items-center text-xs text-gray-500'>
            <Lock className='w-3 h-3' />
            Locked — already released
          </span>
        )}
      </div>

      <div className='flex gap-4 items-center'>
        <label className='flex gap-2 items-center text-sm cursor-pointer'>
          <input
            type='radio'
            name={`audience-${index}`}
            checked={affectsAll}
            disabled={disabled}
            onChange={() => setAffectsAll(true)}
          />
          All players
        </label>
        <label className='flex gap-2 items-center text-sm cursor-pointer'>
          <input
            type='radio'
            name={`audience-${index}`}
            checked={!affectsAll}
            disabled={disabled}
            onChange={() => setAffectsAll(false)}
          />
          Specific players
        </label>
      </div>

      {!affectsAll && (
        <div className='space-y-2'>
          {assignable.length === 0 ? (
            <p className='text-sm text-gray-500'>
              No players have joined this room yet.
            </p>
          ) : (
            <div className='overflow-y-auto p-2 space-y-1 max-h-48 bg-white rounded border border-gray-200'>
              {assignable.map((player) => (
                <label
                  key={player.memberId}
                  className='flex gap-2 items-center p-1 text-sm rounded cursor-pointer hover:bg-gray-50'
                >
                  <Checkbox
                    checked={selected.includes(player.memberId)}
                    disabled={disabled}
                    onChange={() => togglePlayer(player.memberId)}
                  />
                  <span className='text-dark-blue'>
                    {player.member_name || player.member_email}
                  </span>
                </label>
              ))}
            </div>
          )}
          {noneChosen && (
            <p className='text-sm text-red-500'>
              Choose at least one player, or set this back to all players.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
